import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./styles.scss";

import Modal01 from "components/Modal/Modal01";
import Modal02 from "components/Modal/Modal02";
import { ekycSession, ekycResult } from "module/ekycModule";
import { LOGOUT } from "module/userModule";

const USEB_ORIGIN = "https://kyc.useb.co.kr";

const EkycStart = (props) => {
    const dispatch = useDispatch();
    const estimate = useSelector((state) => state.contract.estimate);
    const token = useSelector((state) => state.pass.accessToken);
    const personal = useSelector((state) => state.contract.isPersonal);
    const corpAuthType = useSelector((state) => state.ekyc.corpAuthType);
    const transactionId = useSelector((state) => state.ekyc.transactionId);
    const accessToken = useSelector((state) => state.ekyc.accessToken);
    const errorCode = useSelector((state) => state.ekyc.errorCode);
    const fieldLabel = useSelector((state) => state.ekyc.fieldLabel);
    const kunnr = useSelector((state) => state.contract.KUNNR);
    const katr7 = useSelector((state) => state.contract.E_KATR7);
    const egubun = useSelector((state) => state.contract.EGUBUN);
    const successorGubun = useSelector((state) => state.ekyc.successorGubun);

    const iframeRef = useRef(null);

    // result_type 2(자동거부)/5(심사대기) 수신 시 complete 메시지로 인한 잘못된 이동 방지
    const shouldNavigateOnCompleteRef = useRef(true);

    // "success" postMessage에서 받은 review 데이터 캐시 — "complete" 호출 시 함께 전송
    const cachedReviewRef = useRef(null);

    // credentials 이미 전송했는지 여부 — iframe 내부 이동으로 인한 중복 전송 방지
    const credentialsSentRef = useRef(false);

    const [iframeSrc, setIframeSrc] = useState(null);
    const [sessionErrorModal, setSessionErrorModal] = useState(false);
    const [dataErrorModal, setDataErrorModal] = useState(false);
    const [fieldErrorModal, setFieldErrorModal] = useState(false);
    const [fieldErrorMsg, setFieldErrorMsg] = useState("");
    const [failModal, setFailModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [failMsg, setFailMsg] = useState("");

    const getAuthType = () => {
        if (personal) return "personal";
        if (corpAuthType === "delegate") return "corp_delegate";
        return "corp_rep";
    };

    const requestSession = () => {
        setIframeSrc(null);
        shouldNavigateOnCompleteRef.current = true;
        credentialsSentRef.current = false;
        // LM/LF → I_ETMTSEQ, HA/HC/OR 또는 승계 → I_VBELN
        const isVbelnType = successorGubun !== "" || ["HA", "HC", "OR", "HH"].includes(egubun);
        const authType = getAuthType();
        dispatch(
            ekycSession.post.request({
                estimate: isVbelnType ? undefined : estimate,
                vbeln:    isVbelnType ? estimate  : undefined,
                token,
                authType,
                kunnr,
                katr7,
            })
        );
    };

    const getFailMessage = (errorCode) => {
        const map = {
            CE002: "신분증을 다시 촬영해주세요.",
            CE003: "신분증을 다시 촬영해주세요.",
            CE004: "얼굴 인증에 실패했습니다.",
            CE005: "본인 확인에 실패했습니다.",
            CE006: "본인 확인에 실패했습니다.",
            CF016: "일반 계좌번호로 다시 시도해주세요.",
        };

        return map[errorCode] || "인증에 실패했습니다.";
    };

    const handleResultType = (resultType) => {
        const normalizedResultType = Number(resultType);

        // complete 메시지가 와도 /ekyc/complete로 이동하지 않음
        if (normalizedResultType === 2) {
            shouldNavigateOnCompleteRef.current = false;
        }

        if (normalizedResultType === 2) {
            setRejectModal(true);
            return;
        }

        // resultType === 1 또는 5(심사대기): 자동승인과 동일하게 처리
        shouldNavigateOnCompleteRef.current = true;
    };

    const decodeUsebMessage = (data) => {
        if (typeof data !== "string") return null;

        try {
            const decodedData = decodeURIComponent(atob(data));
            return JSON.parse(decodedData);
        } catch (error) {
            return null;
        }
    };

    useEffect(() => {
        if (!estimate) {
            props.history.push("/auth/contractNum");
            return;
        }

        requestSession();
    }, []);

    useEffect(() => {
        if (transactionId && accessToken) {
            setIframeSrc(`${USEB_ORIGIN}/auth`);
        }
    }, [transactionId, accessToken]);

    const handleIframeLoad = () => {
        if (!transactionId || !accessToken || !iframeRef.current) {
            return;
        }
        if (credentialsSentRef.current) {
            return;
        }
        credentialsSentRef.current = true;
        const params = { access_token: accessToken, transaction_id: transactionId };
        const encoded = btoa(encodeURIComponent(JSON.stringify(params)));
        console.log("[eKYC] postMessage params:", decodeURIComponent(atob(encoded)));
        iframeRef.current.contentWindow.postMessage(encoded, USEB_ORIGIN);
    };

    useEffect(() => {
        if (errorCode === "SESSION_FAIL") {
            setSessionErrorModal(true);
        } else if (errorCode === "DATA_ERROR") {
            setDataErrorModal(true);
        } else if (errorCode === "FIELD_MISSING") {
            setFieldErrorMsg(fieldLabel || "인증 정보");
            setFieldErrorModal(true);
        }
    }, [errorCode]);


    // postMessage 수신 핸들러 (Mobile/PC 공통)
    useEffect(() => {
        const handleMessage = (e) => {
            if (e.origin !== USEB_ORIGIN) return;

            const decoded = decodeUsebMessage(e.data);
            if (!decoded) return;

            const resultType = decoded.review_result?.result_type;

            if (decoded.result === "success") {
                cachedReviewRef.current = {
                    ekycId: decoded.review_result?.id || "",
                    resultType,
                    reviewResult: decoded.review_result,
                };

                dispatch(
                    ekycResult.post.request({
                        transactionId,
                        ekycId: decoded.review_result?.id || "",
                        result: "success",
                        resultType,
                        reviewResult: decoded.review_result,
                        estimate,
                        token,
                    })
                );

                handleResultType(resultType);
                return;
            }

            if (decoded.result === "failed") {
                setIframeSrc(null);
                shouldNavigateOnCompleteRef.current = false;
                dispatch(
                    ekycResult.post.request({
                        transactionId,
                        ekycId: decoded.review_result?.id || "",
                        resultType: decoded.review_result?.result_type,
                        result: "failed",
                        estimate,
                        token,
                    })
                );
                const msg = getFailMessage(
                    decoded.error_code ||
                    decoded.api_response?.result_code
                );
                setFailMsg(msg);
                setFailModal(true);
                return;
            }

            if (decoded.result === "complete") {
                dispatch(
                    ekycResult.post.request({
                        transactionId,
                        result: "complete",
                        estimate,
                        token,
                        ...(cachedReviewRef.current || {}),
                    })
                );
                if (shouldNavigateOnCompleteRef.current) {
                    props.history.push("/ekyc/complete");
                }
                return;
            }

            if (decoded.result === "close") {
                setIframeSrc(null);
                dispatch(
                    ekycResult.post.request({
                        transactionId,
                        result: "close",
                        estimate,
                        token,
                    })
                );
                setCancelModal(true);
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [transactionId, estimate, token, dispatch, props.history]);

    const handleRetry = () => {
        setFailModal(false);
        setCancelModal(false);
        setRejectModal(false);
        requestSession();
    };

    const handleRejectClose = () => {
        setRejectModal(false);
        props.history.push("/");
    };

    return (
        <>
            {iframeSrc && (
                <iframe
                    ref={iframeRef}
                    id="kyc_iframe"
                    title="UseB eKYC"
                    allow="camera"
                    src={iframeSrc}
                    onLoad={handleIframeLoad}
                    className={styles.iframe}
                />
            )}

            <Modal01
                open={sessionErrorModal}
                close={() => {
                    setSessionErrorModal(false);
                    requestSession();
                }}
                header="인증 시작 실패"
            >
                인증 시작에 실패했습니다.
                <br />
                잠시 후 다시 시도해주세요.
            </Modal01>

            <Modal01
                open={dataErrorModal}
                close={() => {
                    setDataErrorModal(false);
                    dispatch({ type: LOGOUT.GET.SUCCESS });
                }}
                header="인증 오류"
            >
                고객 정보 오류로 인해 인증을 진행할 수 없습니다.
                <br />
                담당자에게 문의해주세요.
            </Modal01>

            <Modal01
                open={fieldErrorModal}
                close={() => {
                    setFieldErrorModal(false);
                    dispatch({ type: LOGOUT.GET.SUCCESS });
                }}
                header="시스템 에러"
            >
                담당 영업매니저에게 문의하십시오.
                <br />
                오류 : {fieldErrorMsg}
            </Modal01>

            <Modal02
                open={failModal}
                header="인증 실패"
                btnOnclick1={() => setFailModal(false)}
                btnOnclick2={handleRetry}
                text1={"취소"}
                text2={"다시 시도"}
                btnStyle2={{ color: "#DA291C" }}
            >
                {failMsg || "인증에 실패했어요."}
                <br />
                다시 시도해 주세요.
            </Modal02>

            <Modal02
                open={cancelModal}
                header="인증 중단"
                btnOnclick1={() => props.history.push("/")}
                btnOnclick2={handleRetry}
                text1={"처음으로"}
                text2={"다시 시도"}
                btnStyle2={{ color: "#DA291C" }}
            >
                인증을 중단하셨습니다.
                <br />
                다시 시도하시겠습니까?
            </Modal02>

            <Modal01
                open={rejectModal}
                close={handleRejectClose}
                header="본인인증 실패"
            >
                본인 인증에 실패하였습니다.
            </Modal01>

        </>
    );
};

export default EkycStart;
